<!-- the node types fold away to give the table more room (a button of our own: the material panel kept hiding it) -->
<button type="button" class="types-header" (click)="toggleTypes()" [attr.aria-expanded]="expandedTypes"
  [matTooltip]="expandedTypes ? 'Hide the node types to give the table more room' : 'Show the node types'">
  <mat-icon>{{expandedTypes ? 'expand_less' : 'expand_more'}}</mat-icon>
  <h2>Node types</h2>
  <span class="types-selected" [class.none]="!selectedTypeData">{{selectedTypeData ? selectedTypeData.name : 'None chosen'}}</span>
</button>

<div [ngClass]="expandedTypes?'expanded':'hidden'">
    <app-node-types [linqset]="linqset" [comparison]="comparison" [graph]="graphComponent?.graph" [tags]="linqset?.filter?.nodeTags" (selectTypeEvent)="selectType($event)" (loadingTypeEvent)="onTypeLoading($event)"></app-node-types>
</div>

@if (isHelpOn() && selectedTypeData) {
  <div class="hint">You can filter the list shown by typing in the box below. Click on option buttons to switch between table or edit table or charts.</div>
}

@if (typeLoading) {
    <!-- the chosen type's nodes are being fetched -->
    <div #tableContainer [ngClass]="expandedTypes?'table-container-expanded':'table-container-collapsed'" [style.height.px]="tableHeight">
        <spinner-overlay [overlay]="true"></spinner-overlay>
    </div>
}@else if (linqset && selectedTypeData) {
    <div class="controls-container" [class.narrow]="narrow">
        @if (narrow) {
        <mat-form-field class="view-field" subscriptSizing="dynamic">
            <mat-label>View</mat-label>
            <mat-select [(ngModel)]="display" (selectionChange)="changeDisplay()" aria-label="Whether to show table, edit table or charts">
                <mat-option value="insights">Node insights</mat-option>
                @if (isRoleSystem()) {<mat-option value="model">Insights by model</mat-option>}
                @if (canEditNodes) {<mat-option value="edit">Edit</mat-option>}
                <mat-option value="bar-charts">Bar chart</mat-option>
                <mat-option value="bubble-charts">Bubble chart</mat-option>
                @if (hasCapacity()) {<mat-option value="capacity-charts">Capacity</mat-option>}
                <mat-option value="pareto-charts">Pareto</mat-option>
                @if (hasModelSpread()) {<mat-option value="heatmap-charts">Heatmap</mat-option>}
                @if (canCompare()) {<mat-option value="compare-charts">Before and after</mat-option>}
            </mat-select>
        </mat-form-field>
        } @else {
        <mat-button-toggle-group [(ngModel)]="display" aria-label="Whether to show table, edit table or charts" (change)="changeDisplay()" hideSingleSelectionIndicator>
            <mat-button-toggle value="insights" matTooltip="Insights for each node">Node insights</mat-button-toggle>
            @if (isRoleSystem()) {
                <mat-button-toggle value="model" matTooltip="Insights for each model the node appears in">Insights by model</mat-button-toggle>
            }
            @if (canEditNodes) {
                <mat-button-toggle value="edit" matTooltip="Provide a grid to enable editing the properties of each node">Edit</mat-button-toggle>
            }
            <mat-button-toggle value="bar-charts" matTooltip="Create bar chart for selected attributes">Bar chart</mat-button-toggle>
            <mat-button-toggle value="bubble-charts" matTooltip="Create bubble chart for selected attributes">Bubble chart</mat-button-toggle>
            @if (hasCapacity()) {
                <mat-button-toggle value="capacity-charts" matTooltip="How loaded each is: its work against its capacity">Capacity</mat-button-toggle>
            }
            <mat-button-toggle value="pareto-charts" matTooltip="The few that make up most of an attribute, largest first">Pareto</mat-button-toggle>
            @if (hasModelSpread()) {
                <mat-button-toggle value="heatmap-charts" matTooltip="A heatmap of each node against the models it works in">Heatmap</mat-button-toggle>
            }
            @if (canCompare()) {
                <mat-button-toggle value="compare-charts" matTooltip="Each node in current state and in the project compared with, largest change first">Before and after</mat-button-toggle>
            }
        </mat-button-toggle-group>
        }
        @if (isTableDisplay()) {
            <mat-form-field class="filter-field" subscriptSizing="dynamic">
                <mat-icon matPrefix>search</mat-icon>
                <input matInput (keyup)="applyFilter($event)" placeholder="Filter nodes" aria-label="Filter nodes by name, description, model, role or system" #input>
                @if (dataSource.filter) {
                    <button matSuffix mat-icon-button aria-label="Clear filter" (click)="clearFilter(input)"><mat-icon>close</mat-icon></button>
                }
            </mat-form-field>
        }
        <span class="table-actions">
            @if (isTableDisplay()) {
                @if (narrow) {
                <button mat-icon-button color="accent" [matMenuTriggerFor]="columnMenu" [matTooltip]="'Choose which columns to show' + (hiddenColumnCount() > 0 ? ' (' + hiddenColumnCount() + ' hidden)' : '')"
                    aria-label="Choose which columns to show" data-testid="node-columns-button"><mat-icon>view_column</mat-icon></button>
                } @else {
                <button mat-button color="accent" [matMenuTriggerFor]="columnMenu" matTooltip="Choose which columns to show" data-testid="node-columns-button">
                    <mat-icon>view_column</mat-icon>Columns{{hiddenColumnCount() > 0?' (' + hiddenColumnCount() + ' hidden)':''}}
                </button>
                }
                <mat-menu #columnMenu="matMenu">
                    @for (column of optionalColumns; track column) {
                        <div mat-menu-item (click)="$event.stopPropagation(); toggleColumn(column)">
                            <mat-checkbox [checked]="isColumnVisible(column)" (click)="$event.stopPropagation()" (change)="toggleColumn(column)">{{headings[column]}}</mat-checkbox>
                        </div>
                    }
                    <button mat-menu-item (click)="showAllColumns()">Show all columns</button>
                </mat-menu>
            }
            @if (narrow) {
            <button mat-icon-button color="accent" (click)="exportTable()" matTooltip="Export the nodes in the table to a CSV file" aria-label="Export the nodes to a CSV file"><mat-icon>download</mat-icon></button>
            } @else {
            <button mat-button color="accent" (click)="exportTable()" matTooltip="Export the nodes in the table to a CSV file"><mat-icon>download</mat-icon>Export CSV</button>
            }
        </span>
    </div>

    @if (dataSource.filter && isTableDisplay()) {
        <div class="filter-count">Showing {{dataSource.filteredData.length}} of {{dataSource.data.length}} nodes</div>
    }

    @if (isChartDisplay()) {
        <app-model-charts [linqset]="linqset" [trigger]="trigger" [comparison]="comparison" [chartType]="chartType()" [selectedTypeData]="selectedTypeData" (selectNodeEvent)="selectNodeKey($event)"></app-model-charts>
    }@else if (display === 'insights' || display === 'model' || display === 'edit') {
        @if (isHelpOn()) {
            <div class="hint">Click on a node (row) will show the edges in and out of the node. Checking the box on a row will select that node in the model.</div>
        }
        <div #tableContainer [ngClass]="expandedTypes?'table-container-expanded':'table-container-collapsed'" class="fits-rows" [style.max-height.px]="tableHeight">
            @if (loading) {
                <spinner-overlay [overlay]="true"></spinner-overlay>
            }@else {
                <table mat-table [dataSource]="dataSource" matSort>
                    @for (column of displayedColumns; track column; let i = $index) {
                        <!-- the name, and the checkbox and icon before it, stay in view as the table scrolls across -->
                        <ng-container matColumnDef="{{column}}" [sticky]="column === 'select' || column === 'icon' || column === 'name'">
                            @if (column === 'select') {
                                <th mat-header-cell *matHeaderCellDef>
                                    <mat-checkbox (change)="$event ? toggleAllRows() : null"
                                        [(ngModel)]="allSelected"
                                        [indeterminate]="selection.hasValue() && !isAllSelected()"
                                        [aria-label]="checkboxLabel()">
                                    </mat-checkbox>
                                </th>
                                <td mat-cell *matCellDef="let row">
                                    @if (row.change !== "Removed") {
                                        <mat-checkbox (click)="$event.stopPropagation()"
                                            (change)="$event ? selection.toggle(row) : null; sendSelectEvent()"
                                            [checked]="selection.isSelected(row)"
                                            [aria-label]="checkboxLabel(row)">
                                        </mat-checkbox>
                                    }
                                </td>
                                <td mat-footer-cell *matFooterCellDef></td>
                            }@else if (column === 'icon') {
                                <th mat-header-cell *matHeaderCellDef mat-sort-header [attr.colspan]="getHeadingWidth(column)" [style]="getHeaderStyle(column)"> {{headings[column]}} </th>
                                <td mat-cell *matCellDef="let node">
                                    @if (node.icon) {
                                        <img [src]="node.icon" height="28" alt=""/>
                                    }@else if (node.node?.type) {
                                        <app-node-svg [type]="node.node.type" [error]="node.warnings?.length > 0" [noText]="true" [height]="24" [caller]="'Node table'"/>
                                    }
                                </td>
                                <td mat-footer-cell *matFooterCellDef></td>
                            }@else {
                                <!-- a comparison column is headed by its workspace, whose name can be long: it's shortened, with the whole name on hover -->
                                <th mat-header-cell *matHeaderCellDef mat-sort-header [matTooltip]="column.endsWith('2') ? headings[column] : ''">
                                    <span [class.workspace-heading]="column.endsWith('2')">{{headings[column]}}</span>
                                </th>
                                @if (display === 'edit') {
                                    <td mat-cell *matCellDef="let node">
                                        @if (column === "model") {
                                            <div class="name-container"><span>{{node[column]?.name||''}}</span></div>
                                        }@else if (node.change === "Removed" || node.readonly) {
                                            <div class="name-container"><span>{{node.node[column]}}</span></div>
                                        }@else if (isExternalRole(node) && (column === 'duration' || column === 'frequency' || column === 'percentageModelled')) {
                                            <!-- an external organisation's time and availability aren't the organisation's to set -->
                                        }@else if (column === 'duration' || column === 'frequency') {
                                            <button mat-button color="accent" (click)="openFrequencyDialog(node)" style="align-items: left;align-content: left;text-align: left;">
                                                {{node[column]}}
                                            </button>
                                        }@else if (column === 'tagKeys') {
                                            <mat-select multiple [value]="node.tagKeys" placeholder="Select Tags" (selectionChange)="tagSelectionChange(node.node, $event.value)">
                                            @for (group of tags; track group) {
                                                <mat-optgroup [label]="group.name">
                                                @for (tag of group.tags; track tag._key) {
                                                    <mat-option [value]="tag._key">
                                                    {{tag.name}}
                                                    </mat-option>
                                                }
                                                </mat-optgroup>
                                            }
                                            </mat-select>
                                        }@else if (column === 'workType') {
                                            @if (node.node.superType === NODE_SUPERTYPE.ACTION) {
                                                <mat-select [value]="node.node.properties?.workType === 'normal' ? 'value-adding' : node.node.properties?.workType || ''" (selectionChange)="workTypeChanged(node.node, $event.value)">
                                                    <mat-option value="">{{node.workType}}</mat-option>
                                                    @for (w of workTypes; track w.kind) {<mat-option [value]="w.kind">{{w.name}}</mat-option>}
                                                </mat-select>
                                            }
                                        }@else if (column === 'asset') {
                                            <mat-select [(value)]="node.node.informationAssetKey" placeholder="Select asset" (selectionChange)="fieldChanged(node.node, column)">
                                                @for (asset of linqset.organisation.informationAssets; track asset._key) {
                                                    <mat-option [value]="asset._key">{{asset.name}}</mat-option>
                                                }
                                            </mat-select>
                                        }@else if (column === 'name' || column === 'description' || column === 'url') {
                                            <input [type]="getFieldType(column)" [(ngModel)]="node.node[column]" (change)="fieldChanged(node.node, column)">
                                        }@else if (!node.insights.hasPeople) {
                                            @if (column === 'people' && node.node.type === NODE_TYPE.PERSON) {
                                                <div class="name-container"><span>1</span></div>
                                            }@else if ((column !== 'outputRevenue' && column !== 'outputValue') || (node.insights.isOutput && !node.insights.joinedOut)) {
                                                <input [type]="getFieldType(column)" [(ngModel)]="node.node.properties[column]" [placeholder]="getPlaceHolder(column)" (change)="fieldChanged(node.node, column)">
                                            }
                                        }@else {
                                            <div class="name-container"><span>Uses people</span></div>
                                        }
                                    </td>
                                    <td mat-footer-cell *matFooterCellDef></td>
                                }@else {
                                    @if (column === 'name') {
                                        <td mat-cell *matCellDef="let node" [matTooltip]="getWarningDescription(node)">
                                            <div class="name-container"><span>{{node.name}}</span></div>
                                        </td>
                                        <td mat-footer-cell *matFooterCellDef> Total </td>
                                    }@else if (column === 'description') {
                                        <td mat-cell *matCellDef="let node" [matTooltip]="getWarningDescription(node)">
                                            <div class="name-container"><span>{{node.description}}</span></div>
                                        </td>
                                        <td mat-footer-cell *matFooterCellDef></td>
                                    }@else if (column === 'models') {
                                        <td mat-cell *matCellDef="let node" [matTooltip]="node.modelNames.join(', ')">
                                            {{node.models | number}}
                                        </td>
                                        <td mat-footer-cell *matFooterCellDef></td>
                                    }@else {
                                        <td mat-cell *matCellDef="let node" [style]="getNumberStyle(node.insightsValues, column)">
                                            @if (column === 'tag') {
                                                @if (node.tags) {
                                                    @for (tag of node.tags; track tag._key) {
                                                        <span class="tag-chip"><i class="fa fa-solid fa-tag" [style]="getColourStyleForTag(tag)"></i>{{tag.name}}</span>
                                                    }
                                                }
                                            }@else if (column === 'model') {
                                                @if (node[column]) {
                                                    <a class="model-link" href="" (click)="$event.preventDefault(); $event.stopPropagation(); gotoModel(node)" matTooltip="Open this model">{{node[column].name}}</a>
                                                }
                                            }@else if (column === 'change' || column === 'asset' || column === 'frequency' || column === 'duration' || column === 'processingType' || column === 'workType' || column === 'notes' || column === 'url' || column === 'role' || column === 'system' || column === 'from') {
                                                {{node[column]}}
                                            }@else if (isMoney(column)) {
                                                {{node.insightsValues[column] | currency: linqset.organisation.currencyCode||'NZD': 'symbol-narrow': '1.0-0'}}
                                            }@else {
                                                {{node.insightsValues[column] | number}}
                                            }
                                        </td>
                                        <td mat-footer-cell *matFooterCellDef [style]="getNumberStyle(totals, column)"> {{isMoney(column)?(totals[column] | currency: linqset.organisation.currencyCode||'NZD': 'symbol-narrow': '1.0-0'):(totals[column] | number)}} </td>
                                    }
                                }
                                
                            }
                        </ng-container>
                    }

                    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky:true"></tr>
                    <tr mat-row *matRowDef="let element; columns: displayedColumns;"
                        (dblclick)="updateNode(element)"
                        (click)="$event ? selection.toggle(element) : null; sendSelectEvent()"
                        [class.removed-row]="element.change === 'Removed'"
                        [class.selected-row]="multiSelect && selection.isSelected(element)">
                    </tr>
                    <tr mat-footer-row *matFooterRowDef="displayedColumns; sticky:true"></tr>
                </table>
            }
        </div>
    }
}
